import { useTheme } from '@/context/ThemeContext'
import { useLocation } from 'react-router-dom'
import { logout } from '@/services/authService'
import { BreakSelector } from '@/components/controls/BreakSelector'
import { ProvinceSelector } from '@/components/controls/ProvinceSelector'

interface HeaderProps {
  title?: string
  onLogout?: () => void
}

export function Header({ title = 'CPAWS Terrestrial Conservation Study — Private Dashboard', onLogout }: HeaderProps) {
  const { theme, toggleTheme, showIndicator, hideIndicator } = useTheme()
  const location = useLocation()

  // Detect if on scatterplot page to show scatterplot-specific breaks
  const isScatterplotPage = location.pathname.includes('scatterplot')

  const handleLogout = () => {
    logout()
    onLogout?.()
    window.location.hash = '#/login'
  }

  const handleDownloadData = async () => {
    try {
      const response = await fetch('/data/totals.json')
      const data = await response.json()

      // Convert JSON to CSV format
      const csvRows: string[] = []
      csvRows.push('Question,Response,Percentage,Sample Size')

      for (const [questionId, questionData] of Object.entries(data)) {
        const qData = questionData as { responses: Array<{ label: string; percentage: number }>; sampleSize?: number }
        if (qData.responses && Array.isArray(qData.responses)) {
          for (const response of qData.responses) {
            // Escape commas and quotes in labels
            const escapedLabel = response.label.includes(',') || response.label.includes('"')
              ? `"${response.label.replace(/"/g, '""')}"`
              : response.label
            csvRows.push(`${questionId},${escapedLabel},${response.percentage.toFixed(1)},${qData.sampleSize || ''}`)
          }
        }
      }

      const csvContent = csvRows.join('\n')
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'cpaws-survey-data.csv'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to download dataset:', error)
    }
  }

  return (
    <header className="h-14 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between px-4 sticky top-0 z-10">
      <div className="flex items-center gap-4">
        <img
          src="/images/mcallister_logo.png"
          alt="McAllister Opinion Research"
          className="h-8 w-auto"
        />
        <h1 className="text-lg font-semibold text-slate-900 dark:text-white">
          {title}
        </h1>
      </div>

      <div className="flex items-center gap-4">
        {/* Break Selector - includes Tribes as one of the break options */}
        <BreakSelector includeScatterplotBreaks={isScatterplotPage} />

        {/* Province Filter - separate dropdown for province filtering */}
        <ProvinceSelector />

        {/* Download dataset */}
        <button
          onClick={handleDownloadData}
          className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
          aria-label="Download dataset"
          title="Download dataset (CSV)"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
        </button>

        {/* Theme toggle */}
        <div className="relative">
          {showIndicator && (
            <div className="absolute inset-0 rounded-full ring-2 ring-red-500 ring-offset-2 animate-pulse" />
          )}
          <button
            onClick={() => { toggleTheme(); hideIndicator(); }}
            className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
            aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
          >
            {theme === 'light' ? (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            )}
          </button>
        </div>

        {/* Logout button */}
        <button
          onClick={handleLogout}
          className="p-2 text-slate-500 hover:text-red-600 dark:text-slate-400 dark:hover:text-red-400 transition-colors"
          aria-label="Logout"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
        </button>
      </div>
    </header>
  )
}
